// SEQ-182 Round 4 (security audit): a custom P&L metric registered by an
// organisation (the category editor, T14) needs a `metric_key` that is
// guaranteed to be safe to write and to never collide with a well-known
// static key from `PNL_METRIC_KEYS` in pnlAnalytics.ts. The database now
// enforces `^(operational|expense)\.custom\.[0-9a-f]{8}$` for org rows (a
// CHECK constraint), so this module is the single place that generates and
// recognises that exact shape on the TypeScript side.
//
// `operational.custom.<8 hex>` is for a non-currency custom metric (hours,
// count, percent, ratio); `expense.custom.<8 hex>` is for a CURRENCY custom
// category (a dollar line the user adds to an expense section). Both are
// "custom" because they did not come from the static `PNL_METRIC_KEYS`
// vocabulary; a custom key's `unit`/`metricRole`/`financialInclusion` still
// come from the registry row the category editor writes alongside it, same
// as every other metric -- this module only owns the key's shape.
//
// Run: deno test --allow-read src/lib/pnl/customMetricKey.test.ts
// Run: npm run verify:pnl-custom-metric-key

/** The two custom-key prefixes the database CHECK constraint allows. */
export type CustomMetricKeyKind = "operational" | "expense";

/**
 * The exact pattern the database enforces for a custom key on an
 * organisation's own registry rows: `<kind>.custom.<8 lowercase hex>`.
 * Anchored at both ends, so a key with extra characters before or after is
 * rejected, not just one containing a matching substring.
 */
const CUSTOM_METRIC_KEY_PATTERN = /^(operational|expense)\.custom\.[0-9a-f]{8}$/;

/**
 * Generates a fresh custom metric key of the given kind, e.g.
 * `operational.custom.a1b2c3d4`. Uses the same approach as the category
 * editor's own (pre-existing, component-local) key generator
 * (`src/components/EditExpenseCategoriesModal.tsx`): `crypto.randomUUID()`,
 * hyphens stripped, first 8 hex characters. `crypto.randomUUID()` is
 * available in both the browser and Deno's test runtime (Web Crypto API),
 * so this module has no environment-specific branch.
 *
 * Explicit validation (Round 4 finding): the generated key is checked
 * against `CUSTOM_METRIC_KEY_PATTERN` before being returned, and this
 * function throws rather than silently returning a key the database would
 * reject. `crypto.randomUUID()` always yields lowercase hex, so this should
 * never fire in practice; it exists so a future change to the hex-slicing
 * logic fails loudly here, in a unit test, rather than as a database CHECK
 * violation surfaced to a user mid-save.
 */
export function makeCustomMetricKey(kind: CustomMetricKeyKind): string {
  const hex = crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  const key = `${kind}.custom.${hex}`;
  if (!isCustomMetricKey(key)) {
    throw new Error(`makeCustomMetricKey: generated key "${key}" does not match the database's own pattern; this is a bug in this function, not caller input`);
  }
  return key;
}

/**
 * Whether `key` matches the database's exact custom-key pattern
 * (`^(operational|expense)\.custom\.[0-9a-f]{8}$`). Use this to tell a
 * custom metric key apart from a well-known static key from
 * `PNL_METRIC_KEYS` (pnlAnalytics.ts) -- e.g. before offering "delete" on a
 * category row, or before writing a key the category editor did not itself
 * generate (a paste, an import, or any other externally-sourced value).
 */
export function isCustomMetricKey(key: string): boolean {
  return CUSTOM_METRIC_KEY_PATTERN.test(key);
}
